import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ArrowLeft, MapPin, Package, GraduationCap, Loader2 } from "lucide-react";

export default function CoachingDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/aggregators/coachings/${slug}`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="p-12 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!data) return <div className="p-12 text-center text-slate-500">Coaching not found.</div>;

  return (
    <>
      <SEO
        title={`${data.name} — ${data.count} Smart Infovision Installations | Coaching`}
        description={`${data.name} runs ${data.count} Smart Infovision Digital Boards across ${data.cities.length} cities including ${data.cities.slice(0, 3).join(", ")}.`}
        canonical={typeof window !== "undefined" ? window.location.href : ""}
      />
      <section className="bg-black text-white py-10">
        <div className="si-container">
          <Breadcrumbs items={[
            { label: "Coachings", href: "/coachings" },
            { label: data.name },
          ]} dark />
          <Link to="/coachings" className="text-sm text-blue-200 hover:text-white inline-flex items-center gap-1 mt-3">
            <ArrowLeft size={14} /> Back to all coachings
          </Link>
          <h1 className="mt-3 text-4xl md:text-5xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <GraduationCap size={36} className="text-orange-300" /> {data.name}
          </h1>
          <p className="mt-3 text-white/70">
            <strong>{data.count}</strong> installations across <strong>{data.cities.length}</strong> cities and <strong>{data.states.length}</strong> states.
          </p>
        </div>
      </section>

      <section className="py-10 si-container">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-xs uppercase tracking-wider text-slate-900 font-bold">Cities</p>
            <p className="mt-1 text-2xl font-semibold">{data.cities.length}</p>
            <p className="mt-2 text-xs text-slate-600">{data.cities.slice(0, 5).join(", ")}{data.cities.length > 5 ? "…" : ""}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-4">
            <p className="text-xs uppercase tracking-wider text-emerald-700 font-bold">States</p>
            <p className="mt-1 text-2xl font-semibold">{data.states.length}</p>
            <p className="mt-2 text-xs text-slate-600">{data.states.slice(0, 5).join(", ")}{data.states.length > 5 ? "…" : ""}</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <p className="text-xs uppercase tracking-wider text-slate-700 font-bold">Products</p>
            <p className="mt-1 text-2xl font-semibold">{data.products.length}</p>
            <p className="mt-2 text-xs text-slate-600 line-clamp-2">{data.products.slice(0, 3).join(", ")}{data.products.length > 3 ? "…" : ""}</p>
          </div>
        </div>

        <h2 className="text-2xl font-bold mb-4">All installations</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="installations-grid">
          {(data.installations || []).map((it, i) => (
            <Link
              key={it.id || i}
              to={`/installations/${it.siv || it.id}`}
              className="block bg-white border border-slate-200 hover:border-slate-900 rounded-lg p-4 transition"
            >
              <p className="font-bold text-slate-900 truncate">{it.customer || "—"}</p>
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <MapPin size={11} /> {it.city || "—"}{it.state ? `, ${it.state}` : ""}
              </p>
              {it.product && <p className="text-xs text-slate-600 mt-2 line-clamp-1 flex items-start gap-1"><Package size={11} className="mt-0.5 flex-shrink-0" />{it.product}{it.size ? ` · ${it.size}` : ""}</p>}
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}
